import React, { useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import { Place_T } from "../../types/Place";
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Button,
} from "@mui/material";

// Tile Layers
const tileLayers = {
  street: {
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution:
      '&copy; <a href="https://carto.com/">Carto</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: '&copy; <a href="https://www.esri.com/">Esri</a>',
  },
  streetOverlay: {
    url: "https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png",
    attribution:
      '&copy; <a href="https://carto.com/">Carto</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
};

// Utility function to calculate bounding box for zoom adjustment
const getBoundsFromLocations = (locations: Place_T[]) => {
  if (locations.length === 0) {
    return L.latLngBounds([
      [39.5, -98.35], // Default fallback (center of USA)
      [39.5, -98.35],
    ]);
  }

  return L.latLngBounds(
    locations.map(
      (loc) =>
        loc.coordinates
          ? Array.isArray(loc.coordinates)
            ? loc.coordinates
            : [loc.coordinates.lat, loc.coordinates.lng]
          : [0, 0] // Default coordinates if undefined
    )
  );
};

// Component to adjust zoom dynamically
const AdjustZoom: React.FC<{
  locations: Place_T[];
  onSetBounds: (bounds: L.LatLngBounds) => void;
}> = ({ locations, onSetBounds }) => {
  const map = useMap();

  useEffect(() => {
    if (locations.length > 0) {
      const bounds = getBoundsFromLocations(locations);
      onSetBounds(bounds); // Store initial bounds
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map, onSetBounds]);

  return null;
};

// Hook to track which locations are visible in the current map view
const VisibleLocationsTracker: React.FC<{
  locations: Place_T[];
  onVisibleChange: (visible: Place_T[]) => void;
}> = ({ locations, onVisibleChange }) => {
  const map = useMap();

  useMapEvents({
    moveend: () => updateVisibleLocations(),
    zoomend: () => updateVisibleLocations(),
  });

  const updateVisibleLocations = () => {
    const bounds = map.getBounds();
    const visibleLocations = locations.filter((loc) => {
      if (!loc.coordinates) return false;

      const [lat, lng] = Array.isArray(loc.coordinates)
        ? [loc.coordinates[0], loc.coordinates[1]]
        : [loc.coordinates.lat, loc.coordinates.lng];

      return bounds.contains([lat, lng]);
    });

    onVisibleChange(visibleLocations);
  };

  return null;
};
// Hook to conditionally enable zoom only when Ctrl or right-click is used
const ControlZoomHandler: React.FC = () => {
  const map = useMap();
  const [zoomEnabled, setZoomEnabled] = useState(false);

  useEffect(() => {
    const enableZoom = (e: KeyboardEvent | MouseEvent) => {
      if (
        (e instanceof KeyboardEvent && e.ctrlKey) ||
        (e instanceof MouseEvent && e.button === 2) // Right-click
      ) {
        setZoomEnabled(true);
        map.scrollWheelZoom.enable();
      }
    };

    const disableZoom = () => {
      setZoomEnabled(false);
      map.scrollWheelZoom.disable();
    };

    document.addEventListener("keydown", enableZoom);
    document.addEventListener("keyup", disableZoom);
    document.addEventListener("mousedown", enableZoom);
    document.addEventListener("mouseup", disableZoom);
    document.addEventListener("contextmenu", disableZoom); // Prevents stuck zoom on right-click

    return () => {
      document.removeEventListener("keydown", enableZoom);
      document.removeEventListener("keyup", disableZoom);
      document.removeEventListener("mousedown", enableZoom);
      document.removeEventListener("mouseup", disableZoom);
      document.removeEventListener("contextmenu", disableZoom);
    };
  }, [map]);

  return null;
};
const ResetZoomButton: React.FC<{
  initialBounds: L.LatLngBounds | null;
  onResetZoom: () => void;
}> = ({ initialBounds, onResetZoom }) => {
  const map = useMap();

  const handleResetZoom = () => {
    if (initialBounds) {
      map.fitBounds(initialBounds, { padding: [50, 50] });
      onResetZoom(); // Notify parent to show all locations
    }
  };

  return (
    <Button
      variant="outlined"
      sx={{
        position: "absolute",
        top: "10px",
        right: "10px",
        zIndex: 1000,
        backgroundColor: "white",
      }}
      onClick={handleResetZoom}
      disabled={!initialBounds}
    >
      Reset Zoom
    </Button>
  );
};

type LeafletMapPlacesProps = {
  locations: Place_T[];
  onVisibleChange: (visible: Place_T[]) => void;
  onResetZoom: () => void;
};

const LeafletMapPlaces: React.FC<LeafletMapPlacesProps> = ({
  locations,
  onVisibleChange,
  onResetZoom,
}) => {
  const [mapType, setMapType] = useState<"street" | "satellite">("street");
  const [initialBounds, setInitialBounds] = useState<L.LatLngBounds | null>(
    null
  );

  const mapCenter = useMemo(
    () => getBoundsFromLocations(locations).getCenter(),
    [locations]
  );

  const handleMapTypeToggle = (
    event: React.MouseEvent<HTMLElement>,
    newMapType: "street" | "satellite" | null
  ) => {
    if (newMapType) setMapType(newMapType);
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Toggle Button Group */}
      <ToggleButtonGroup
        color="primary"
        value={mapType}
        exclusive
        onChange={handleMapTypeToggle}
        aria-label="map type toggle"
        sx={{ mb: 1 }}
      >
        <ToggleButton
          value="street"
          aria-label="street view"
          style={{ width: "200px" }}
        >
          <Typography variant="button">Street View</Typography>
        </ToggleButton>
        <ToggleButton
          value="satellite"
          aria-label="satellite view"
          style={{ width: "200px" }}
        >
          <Typography variant="button">Satellite View</Typography>
        </ToggleButton>
      </ToggleButtonGroup>

      <MapContainer
        center={mapCenter}
        zoom={3}
        style={{ height: "750px", width: "100%" }}
      >
        <ControlZoomHandler /> {/* Handles Ctrl + Right Click zooming */}
        {/* Reset Zoom Button */}
        <ResetZoomButton
          initialBounds={initialBounds}
          onResetZoom={onResetZoom}
        />
        {/* Track initial bounds */}
        <AdjustZoom locations={locations} onSetBounds={setInitialBounds} />
        {/* Track visible places */}
        <VisibleLocationsTracker
          locations={locations}
          onVisibleChange={onVisibleChange}
        />
        {/* Base Layer */}
        <TileLayer
          url={tileLayers[mapType].url}
          attribution={tileLayers[mapType].attribution}
        />
        {/* Overlay for Street Names (Only for Satellite View) */}
        {mapType === "satellite" && (
          <TileLayer
            url={tileLayers.streetOverlay.url}
            attribution={tileLayers.streetOverlay.attribution}
            opacity={0.8}
          />
        )}
        {/* Marker Cluster Group */}
        <MarkerClusterGroup>
          {locations.map((location) => {
            if (location.coordinates) {
              const position: [number, number] = Array.isArray(
                location.coordinates
              )
                ? [location.coordinates[0], location.coordinates[1]]
                : [location.coordinates.lat, location.coordinates.lng];
              return (
                <Marker
                  key={location.place_id}
                  position={position}
                  icon={L.icon({
                    iconUrl:
                      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowUrl:
                      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
                    shadowSize: [41, 41],
                  })}
                >
                  <Popup>
                    <b>{location.place_id}</b>
                    <br />
                    {location.name}
                    <br />
                    {location.address}
                  </Popup>
                </Marker>
              );
            }
            return null;
          })}
        </MarkerClusterGroup>
      </MapContainer>
    </Box>
  );
};

export default LeafletMapPlaces;
